import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache, ApolloLink } from '@apollo/client';
import { LocalState } from '@apollo/client/local-state';
import { RetryLink } from '@apollo/client/link/retry';
import { httpStatusOf } from './graph-errors';
import { createGraphqlErrorLink } from './graphql-error-link';
import { NetworkStatusUpdateService } from './network-status/network-status-update.service';
import { compact } from 'lodash';

export interface CreateApolloExtraOptions {
  resolvers?: LocalState.Resolvers
}

export const RETRY_MAX_ATTEMPTS = 5;

// 504 is deliberately absent; see shouldRetryRequest.
const RETRYABLE_STATUSES = [502, 503];

/**
 * Definitions are inspected directly rather than via `getMainDefinition` so this stays off
 * `@apollo/client/utilities`, which we do not otherwise depend on.
 */
export function isMutationOperation(operation?: ApolloLink.Operation): boolean {
  return !!operation?.query?.definitions?.some(
    (definition) => definition.kind === 'OperationDefinition' && definition.operation === 'mutation'
  );
}

/**
 * A gateway timeout means the request was cut off, NOT that the server stopped working.
 * Express does not abort a resolver when the client disconnects, so retrying a mutation
 * stacks a second execution on top of one still running.
 *
 * On 2026-08-27 this turned 8 commission recalcs into 38 concurrent pipelines: each recalc
 * ran past the ingress's 60s ceiling, returned 504, and was retried to the `max` below --
 * on a different pod each time, since every attempt is a new load-balanced request.
 *
 * Queries are not safe to retry on a 504 either. On 2026-10-22 a report query ran a pod out
 * of memory; the dying pod held its socket open until the gateway timed it out, and each
 * retry carried the same query to the next pod. Five retries took out all six pods. A 504
 * says the work was too slow or too big, and sending it again does not change that.
 *
 * 502 and 503 still retry for queries: those mean no pod took the request, so a retry is
 * the first execution rather than a second one. Mutations retry on nothing, since a 502
 * can also mean the pod died partway through running one.
 */
export function shouldRetryRequest(error: unknown, operation?: ApolloLink.Operation): boolean {
  if (isMutationOperation(operation)) {
    return false;
  }

  return RETRYABLE_STATUSES.includes(httpStatusOf(error) ?? -1);
}

/**
 * The client options both the portal factory and the sidecar's `createCpaasApollo` share,
 * so the `@client` resolver wiring and the Apollo Client 3 compatible defaults cannot drift
 * apart between them.
 */
export function createApolloClientDefaults(isProduction: boolean, resolvers: LocalState.Resolvers = {}) {
  return {
    // Apollo Client 4 throws on any `@client` field unless the client has a LocalState.
    localState: new LocalState({
      resolvers: {
        User: {
          nameAndOrganization: (user: { name?: string; organization?: string }) =>
            compact([user.name, user.organization]).join(' - ')
        },
        ...resolvers
      }
    }),
    devtools: { enabled: !isProduction },
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'none',
        // Apollo Client 4 defaults this to true, which adds loading emissions on every
        // refetch and poll. Callers were written against Apollo Client 3's false.
        notifyOnNetworkStatusChange: false
      },
      mutate: {
        errorPolicy: 'none'
      }
    }
  };
}

export function createApollo(
  httpLink: HttpLink, 
  cache: InMemoryCache, 
  networkStatus: NetworkStatusUpdateService, 
  isProduction: boolean,
  options?: CreateApolloExtraOptions
) {
  return {
    link: ApolloLink.from([
      new RetryLink({
        delay: (count, operation, error) => {
          // I've found the default jitter based method to potentially retry too quickly
          // So instead of random between 0ms and calculated delay
          // randomize between previousMaxDelay and the calculated delay for this time
          const baseDelay = 300;
          const minDelay = count === 0 ? baseDelay : baseDelay * 2 ** (count - 1);
          let delay = baseDelay * 2 ** count;
          delay = Math.max(Math.random() * delay, minDelay);
          if (!isProduction) {
            // Kept for dev: this line is how you tell a client-side retry from anything else.
            console.log('delaying', delay);
          }
          return delay;
        },
        attempts: {
          max: RETRY_MAX_ATTEMPTS,
          retryIf: shouldRetryRequest
        }
      }),
      createGraphqlErrorLink(networkStatus, isProduction),
      // graphqlErrorLink,
      httpLink.create({
        uri: 'graphql',
        // opts: {
        //   credentials: 'same-origin'
        // },
      })
    ]),
    cache,
    ...createApolloClientDefaults(isProduction, options?.resolvers)
  }
}